import ModalComponent from '../../../../common-components/ModalComponent/ModalComponent';

import '../../styles/request-usage-rights-modal.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getUsageRightsTemplates } from '@/services/inboundEmailTemplate.service';
import { system_templates } from '@/utils/ts/stringUtils';
import { css } from '@emotion/css';
import { MenuItem } from '@mui/material';
import { NotificationManager } from 'react-notifications';
import ReactQuill, { Quill } from 'react-quill';
import { useSelector } from 'react-redux';

import ShortString from '@/components/common-components/ShortString/ShortString';
import { UserNameShow } from '@/components/Dashboard/inbound/components/UserNameDropDown';

import { sendUsageRequest } from '../../../../../services/postTracking.service';
import { formatCurrency, formatCustomParamsInUsageRights, getCurrencyLogo } from '../../../../../utils/global';
import ButtonComponent from '../../../../common-components/ButtonComponent/ButtonComponent';
import SelectInput from '../../../../common-components/SelectInput/SelectInput';
import { useCampaignDetail } from '../CampaignDetails/CampaignDetailProvider';
import { DeleteTemplateModal } from '../CampaignDetails/SelectTemplate/DeleteTemplateModal';
import TemplateModal from '../CampaignDetails/TemplateModal';
import ContentCard from '../ContentCard';
import PayWithProducts from '../UsageRight/PayWithProducts';
import SelectVariant from '../UsageRight/SelectVariant';
import EmailToolbar from './EmailToolbar';

const formats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link', 'strike', 'image', 'indent', 'color'];

export default function RequestUsageRightsModal({ onClose, post, refreshLogPosts }) {
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [duration, setDuration] = useState(-1);
  const [purpose, setPurpose] = useState('none');
  const [payment, setPayment] = useState('none');

  const [isSelectVariantOpen, setIsSelectVariantOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [allOptions, setAllOptions] = useState({});
  const { userDetails } = useSelector((state) => state.user);
  const {
    updatePost,
    refetchUsageRequests,
    currentModal,
    setCurrentModal,
    deleteTemplateModal,
    setDeleteTemplateModal,
    selectedTemplate,
    setSelectedTemplate,
    createNewModal,
    setCreateNewModal,
    isCreateNewModal,
    setIsCreateNewModal,
    isSystemTemplate,
    setIsSystemTemplate,
    message,
    setMessage,
  } = useCampaignDetail();
  const currency = userDetails?.defaultCurrency;

  const modules = {
    toolbar: {
      container: '#email-editor-toolbar-container', //!TODO: put some id here
    },
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
  };

  const [usageRightsTemplates, setUsageRightsTemplates] = useState([]);

  const fetchUsageRightsTemplates = async () => {
    const response = await getUsageRightsTemplates();
    if (response?.status) {
      setUsageRightsTemplates(response?.data);
    }
  };

  const getVariantDetails = (id, variants) => {
    if (variants[0]?.option1 === 'Default Title' || variants.length === 1) {
      return variants[0];
    }
    const selectedVariant = variants?.find((product) => {
      if (product?.option1 && product?.option2 && product?.option3) {
        return (
          product?.option1 === allOptions[id]?.option1 &&
          product?.option2 === allOptions[id]?.option2 &&
          product?.option3 === allOptions[id]?.option3
        );
      }
      if (product?.option1 && product?.option2) {
        return product?.option1 === allOptions[id]?.option1 && product?.option2 === allOptions[id]?.option2;
      }
      if (product?.option1 === allOptions[id]?.option1) {
        return product?.option1 === allOptions[id]?.option1;
      }
      return null;
    });
    return selectedVariant;
  };

  async function sendRequest() {
    setIsSendingRequest(true);
    const body = {
      postId: post._id,
      duration: +duration,
      purpose,
      payment: +payment,
      message,
    };
    let ugcFreeProducts;

    if (payment === 'product') {
      body.payment = null;
      ugcFreeProducts = selectedProduct.map((product) => {
        const variant = getVariantDetails(product.id, product.variants);
        const variantDetailObject = {};
        if (product?.variant?.[0] && product?.variant?.[0]?.option1 !== 'Default Title') {
          if (product?.options?.length > 0) {
            product.options.forEach((option, index) => {
              variantDetailObject[option.name] = allOptions[product.id][`option${index + 1}`];
            });
          }
        }
        return {
          variantId: variant?.variantId || product?.id,
          productId: product?.id,
          productDetails: {
            productImage: product?.image,
            productName: product?.name,
            productPrice: variant?.price,
            variantDetail: variantDetailObject,
          },
          quantity: quantities[product?.id],
        };
      });
      body.ugcFreeProducts = ugcFreeProducts;
    }

    const res = await sendUsageRequest(body);

    if (res.status) {
      NotificationManager.success('Usage Rights Request Sent');
      if (updatePost) {
        updatePost(post._id, {
          usageRightStatus: 'pending',
          usageRequest: {
            duration: +duration,
            purpose,
            payment: +payment,
            message,
            lastSent: new Date().toISOString(),
            ugcFreeProducts,
          },
        });
      }
      if (refreshLogPosts) {
        refreshLogPosts();
      }
      if (refetchUsageRequests) {
        refetchUsageRequests();
      }
      onClose();
    } else {
      NotificationManager.error(res?.data?.message ?? 'Failed To Send Usage Rights Request');
    }

    setIsSendingRequest(false);
  }

  const quillRef = useRef(null);

  const isSubmitButtonDisabled =
    purpose === 'none' ||
    payment === 'none' ||
    message?.length === 0 ||
    (payment === 'product' && selectedProduct.length === 0);
  // editor code

  const replaceCustomParams = useCallback(
    (val) =>
      formatCustomParamsInUsageRights({
        template: val,
        name: post?.profile?.name,
        username: userDetails?.first_name + ' ' + userDetails?.last_name,
      }),
    [post?.profile]
  );

  const handleTemplateSelect = (template) => {
    let body = template?.content || template?.body;
    body = replaceCustomParams(body);
    setMessage(body);
  };

  useEffect(() => {
    fetchUsageRightsTemplates();
  }, [currentModal]);

  if (!isSelectVariantOpen)
    return (
      <>
        <ModalComponent
          show={true && !deleteTemplateModal && !createNewModal}
          onClose={onClose}
          width={912}
          style={{
            padding: '0',
          }}
          bgPadding
          topCross
          outsideClickClose={false}
          bodyStyle={{
            padding: '0',
          }}>
          <div className="request-usage-rights-modal">
            <div className="request-usage-rights-modal__left">
              <div className="request-usage-rights-modal__header">
                <h2>Request for Usage Rights!</h2>
                <p>Add your terms and we will send an email to the creators </p>
              </div>
              <div className="request-usage-right-left-content">
                <div className="request-usage-rights-modal__section">
                  <p className="request-usage-rights-modal__section_desc required-field">
                    Select the duration for which you want the rights
                  </p>
                  <div className="request-usage-rights-modal__row">
                    <label>📅 Duration</label>
                    <SelectInput
                      className="request-usage-rights-modal__row__select"
                      value={duration}
                      customValue
                      customValueInputStyle={{
                        width: '209px',
                      }}
                      customInputType="number"
                      customValuePlaceholder="Enter in days"
                      options={[
                        {
                          value: 30,
                          text: '30 Days',
                        },
                        {
                          value: 60,
                          text: '60 Days',
                        },
                        {
                          value: 90,
                          text: '90 Days',
                        },
                        {
                          value: -1,
                          text: 'Forever',
                        },
                      ]}
                      customValuePostfix={' Days'}
                      onSelect={setDuration}
                    />
                  </div>
                </div>
                <div className="request-usage-rights-modal__section">
                  <p className="request-usage-rights-modal__section_desc required-field">
                    Select the purpose for which you want to use the content.
                  </p>
                  <div className="request-usage-rights-modal__row">
                    <label>📝 Purpose</label>
                    <SelectInput
                      className="request-usage-rights-modal__row__select"
                      value={purpose}
                      onSelect={(e) => {
                        const selected = system_templates.find((s) => s.name === e) || '';
                        setMessage(selected?.body || '');
                        setPurpose(e);
                      }}
                      placeholder="Select Option"
                      placeholderValue="none"
                      customValue
                      customValueInputStyle={{
                        width: '209px',
                      }}
                      customValuePlaceholder="Custom"
                      customInputType="text"
                      options={[
                        'Usage on Ads',
                        'Whitelisted Ads Access',
                        'Organic Repost',
                        'Social Proof on Website',
                      ].map((option) => ({ value: option, text: option }))}
                    />
                  </div>
                </div>
                <div className="request-usage-rights-modal__section">
                  <p className="request-usage-rights-modal__section_desc required-field">
                    Select the incentive you would like to offer in exchange.
                  </p>
                  <div className="request-usage-rights-modal__row">
                    <label>💰 Payment</label>
                    <SelectInput
                      className="request-usage-rights-modal__row__select"
                      value={payment}
                      customValue
                      placeholder="Select Option"
                      placeholderValue="none"
                      customValueInputStyle={{
                        width: '209px',
                      }}
                      customValuePlaceholder="Custom Value"
                      customInputType="number"
                      onSelect={setPayment}
                      customValuePrefix={getCurrencyLogo(currency)}
                      options={[
                        {
                          value: 0,
                          text: 'Request For Free',
                        },
                        {
                          value: 50,
                          text: formatCurrency(50, currency),
                        },
                        {
                          value: 100,
                          text: formatCurrency(100, currency),
                        },
                        {
                          value: 150,
                          text: formatCurrency(150, currency),
                        },
                        {
                          value: 200,
                          text: formatCurrency(200, currency),
                        },
                        {
                          value: 'product',
                          text: 'Send Free Product(s)',
                          ignoreRender: true,
                        },
                      ]}
                      footer={({ handleClose }) => (
                        <SendFreeProductOption
                          onClick={() => {
                            handleClose();
                            setPayment('product');
                          }}
                        />
                      )}
                    />
                  </div>
                  {payment === 'product' && (
                    <PayWithProducts
                      selectedProduct={selectedProduct}
                      setSelectedProduct={setSelectedProduct}
                      setIsSelectVariantOpen={setIsSelectVariantOpen}
                      quantities={quantities}
                      allOptions={allOptions}
                      getVariantDetails={getVariantDetails}
                    />
                  )}
                </div>
                <div className="request-usage-rights-modal__section" style={{ height: '100%' }}>
                  <div className="request-usage-rights-modal__row" style={{ marginBottom: '16px' }}>
                    <label className="required-field">💌 Add A Message For The Creator</label>
                  </div>
                  <div className="message-editor">
                    <ReactQuill
                      ref={quillRef}
                      placeholder="Write your message"
                      theme="snow"
                      style={{ maxHeight: '116px' }}
                      modules={modules}
                      value={message}
                      formats={formats}
                      onChange={(v) => {
                        setMessage(v);
                      }}
                      className={css`
                        .ql-editor {
                          height: auto;
                          max-width: 100% !important;
                        }
                        .ql-container.ql-snow {
                          height: 116px !important;
                          max-height: 116px !important;
                          overflow-y: auto;
                        }
                        * {
                          font-size: 14px;
                        }
                      `}
                    />
                    <EmailToolbar
                      setEmailBody={setMessage}
                      handleTemplateSelect={handleTemplateSelect}
                      templates={usageRightsTemplates}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="request-usage-rights-modal__right">
              <div className="request-usage-rights-modal__right__content-card">
                <div className="avatar-description">
                  <div className="avatar-description__avatar">
                    <img
                      src={post?.profile?.imageUrl}
                      alt="user-avatar"
                      className="avatar-description__avatar__image"
                    />
                  </div>
                  <ShortString name={post?.profile?.username} stringLength={15} />
                </div>
                <ContentCard post={post} hideCardBottom />
              </div>
              <ButtonComponent
                style={{ width: '320px' }}
                showLoading={isSendingRequest}
                onClick={sendRequest}
                disabled={isSubmitButtonDisabled}>
                Send Usage Rights Request
              </ButtonComponent>
            </div>
          </div>
        </ModalComponent>
        <TemplateModal
          show={createNewModal}
          templateType={isSystemTemplate ? 'system' : 'user'}
          onClose={() => {
            setCreateNewModal(false);
            setIsSystemTemplate(false);
            setIsCreateNewModal(false);
          }}
          template={selectedTemplate}
          mode={isCreateNewModal ? 'create' : 'edit'}
        />
        <DeleteTemplateModal
          show={deleteTemplateModal}
          onClose={() => {
            setDeleteTemplateModal(false);
          }}
          setDeleteTemplateModal={setDeleteTemplateModal}
          selectedTemplate={selectedTemplate}
        />
      </>
    );

  if (isSelectVariantOpen)
    return (
      <SelectVariant
        show={isSelectVariantOpen}
        onClose={() => setIsSelectVariantOpen(false)}
        selectedProducts={selectedProduct}
        setSelectedProducts={setSelectedProduct}
        setQuantities={setQuantities}
        quantities={quantities}
        allOptions={allOptions}
        setAllOptions={setAllOptions}
      />
    );
}

const SendFreeProductOption = ({ onClick }) => {
  return (
    <MenuItem
      value="Send Usage Rights Request"
      sx={{
        padding: '8px 8px',
        marginTop: '8px',
        borderRadius: '5px',
      }}
      onClick={onClick}>
      🎁 Send Free Product(s)
    </MenuItem>
  );
};
